/**
 * WebSocket client for virtual-core realtime service.
 * Adapted from platform-tv/src/lib/realtime.ts for web client_kind.
 */

export type WebRealtimeMessage =
  | { type: 'VS_CONNECTED'; clientId: string; timestamp: string }

  | { type: 'VS_EVENT_RESULTS'; schedule_id: string; sport_id: number; scope?: string; week?: number; payload: any }
  | { type: 'VS_EVENT_CANCELLED'; schedule_id: string; sport_id: number; scope?: string; payload: { event_id: number; schedule_id: string } }
  | { type: 'VS_MARKET_TYPES'; payload: any[] }
  | { type: 'VS_JACKPOT'; schedule_id: string; sport_id: number; payload: any }
  | { type: 'VS_USER_WIN'; payload: any }
  | { type: 'VS_PONG'; timestamp: string }
  | { type: 'VS_REJOIN_ACK'; server_timestamp: number }
  | { type: 'VS_ERROR'; message: string };

const MAX_RECONNECT_DELAY_MS = 10_000;
const PONG_TIMEOUT_MS = 5_000;

export function getRealtimeUrl(): string | null {
  if (typeof window === 'undefined') return null;

  const envUrl = (import.meta as any).env?.REACT_APP_REALTIME_URL
    ?? (typeof process !== 'undefined' ? (process as any).env?.REACT_APP_REALTIME_URL : undefined);
  if (envUrl) return envUrl;

  const href = location.href;
  if (href.includes('localhost')) return 'ws://localhost:4004';
  if (href.includes('.caribe7.local')) return 'ws://virtual-sports-realtime.caribe7.local';
  if (href.includes('.caribe7.dev')) return 'wss://virtual-sports-realtime.caribe7.dev';
  if (href.includes('.caribe7.com')) return 'wss://virtual-sports-realtime.caribe7.com';

  return null;
}

export interface RealtimeConnectionHandle {
  close: () => void;
  send: (message: unknown) => void;
  subscribe: (scheduleIds: string[], sessionId?: string | null) => void;
  unsubscribe: (scheduleIds: string[]) => void;
}

export function connectWebRealtime(
  handlers: {
    onConnected?: () => void;
    onDisconnected?: () => void;
    onMessage: (msg: WebRealtimeMessage) => void;
  },
  initialSubscribe?: { scheduleIds: string[]; sessionId?: string | null }
): RealtimeConnectionHandle {
  const url = getRealtimeUrl();
  if (!url) {
    return { close: () => {}, send: () => {}, subscribe: () => {}, unsubscribe: () => {} };
  }

  let ws: WebSocket | null = null;
  let closed = false;
  let reconnectTimer: number | null = null;
  let reconnectAttempt = 0;
  let pingInterval: number | null = null;
  let pendingSubscribe = initialSubscribe ?? null;
  let pongTimeoutTimer: number | null = null;
  let healthCheckInProgress = false;

  const cleanup = () => {
    if (reconnectTimer !== null) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (pingInterval !== null) {
      window.clearInterval(pingInterval);
      pingInterval = null;
    }
    if (pongTimeoutTimer !== null) {
      window.clearTimeout(pongTimeoutTimer);
      pongTimeoutTimer = null;
    }
    healthCheckInProgress = false;
    closed = true;
    if (visibilityHandler) {
      document.removeEventListener('visibilitychange', visibilityHandler);
      visibilityHandler = null;
    }
    if (ws) {
      ws.onopen = null;
      ws.onmessage = null;
      ws.onclose = null;
      ws.onerror = null;
      try {
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, 'Connection closed by client');
        }
      } catch { /* ignore */ }
      ws = null;
    }
  };

  const sendRaw = (message: unknown) => {
    if (closed || !ws || ws.readyState !== WebSocket.OPEN) return;
    try { ws.send(JSON.stringify(message)); } catch { /* ignore */ }
  };

  const startPingInterval = () => {
    if (pingInterval !== null) return;
    pingInterval = window.setInterval(() => {
      sendRaw({ type: 'VS_PING' });
    }, 30_000);
  };

  const stopPingInterval = () => {
    if (pingInterval !== null) {
      window.clearInterval(pingInterval);
      pingInterval = null;
    }
  };

  const doConnect = () => {
    if (closed) return;
    ws = new WebSocket(url);

    ws.onopen = () => {
      if (closed) { try { ws?.close(1000); } catch { /* ignore */ } return; }
      reconnectAttempt = 0;

      if (pendingSubscribe && pendingSubscribe.scheduleIds.length > 0) {
        sendRaw({
          type: 'VS_SUBSCRIBE',
          client_kind: 'web',
          schedule_ids: pendingSubscribe.scheduleIds,
          ...(pendingSubscribe.sessionId ? { session_id: pendingSubscribe.sessionId } : {}),
        });
      }

      startPingInterval();
      handlers.onConnected?.();
    };

    ws.onmessage = (ev) => {
      if (closed) return;
      let msg: WebRealtimeMessage;
      try { msg = JSON.parse(ev.data); } catch { return; }

      // Health-check pong: cancel timeout and send VS_REJOIN
      if (msg.type === 'VS_PONG' && healthCheckInProgress) {
        healthCheckInProgress = false;
        if (pongTimeoutTimer !== null) {
          window.clearTimeout(pongTimeoutTimer);
          pongTimeoutTimer = null;
        }
        sendRaw({ type: 'VS_REJOIN' });
      }

      handlers.onMessage(msg);
    };

    ws.onclose = () => {
      stopPingInterval();
      if (pongTimeoutTimer !== null) {
        window.clearTimeout(pongTimeoutTimer);
        pongTimeoutTimer = null;
      }
      healthCheckInProgress = false;
      if (closed) return;
      handlers.onDisconnected?.();
      reconnectAttempt++;
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempt - 1), MAX_RECONNECT_DELAY_MS);
      reconnectTimer = window.setTimeout(doConnect, delay);
    };

    ws.onerror = () => { /* allow onclose to handle reconnect */ };
  };

  // --- Visibility-aware health check ---
  let visibilityHandler: (() => void) | null = null;

  if (typeof document !== 'undefined') {
    visibilityHandler = () => {
      if (closed) return;
      if (document.visibilityState === 'visible') {
        // Tab became visible: check if WS is still alive
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          // WS already dead; force immediate reconnect
          reconnectAttempt = 0;
          if (reconnectTimer !== null) {
            window.clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          doConnect();
          return;
        }
        if (healthCheckInProgress) return;
        healthCheckInProgress = true;
        sendRaw({ type: 'VS_PING' });
        pongTimeoutTimer = window.setTimeout(() => {
          pongTimeoutTimer = null;
          healthCheckInProgress = false;
          // No pong received: force close -> immediate reconnect
          reconnectAttempt = 0;
          try { ws?.close(4000, 'Health check timeout'); } catch { /* ignore */ }
        }, PONG_TIMEOUT_MS);

        startPingInterval();
      } else {
        // Tab hidden: stop pinging to save battery
        stopPingInterval();
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
  }

  doConnect();

  return {
    close: cleanup,
    send: sendRaw,
    subscribe: (scheduleIds: string[], sessionId?: string | null) => {
      pendingSubscribe = { scheduleIds, sessionId };
      if (!closed && ws && ws.readyState === WebSocket.OPEN) {
        sendRaw({
          type: 'VS_SUBSCRIBE',
          client_kind: 'web',
          schedule_ids: scheduleIds,
          ...(sessionId ? { session_id: sessionId } : {}),
        });
      }
    },
    unsubscribe: (scheduleIds: string[]) => {
      if (!closed && ws && ws.readyState === WebSocket.OPEN) {
        sendRaw({ type: 'VS_UNSUBSCRIBE', schedule_ids: scheduleIds });
      }
    },
  };
}
